import React, { useState } from "react";
import { Box, Typography, IconButton, Drawer } from "@mui/material";
import { MenuBook, ArrowBack, ChevronRight, TravelExplore } from "@mui/icons-material";
import { useTranslate } from "../Translate/TranslateContext";
import MicroclimateSection from "./Microclimate/Microclimate";

const VIEWS = {
    MAIN: "main",
    MICROCLIMATE: "microclimate"
};

export default function ManualMenu() {
    const { translate } = useTranslate();
    const [open, setOpen] = useState(false);
    const [view, setView] = useState(VIEWS.MAIN);

    const expanded = view !== VIEWS.MAIN;

    const handleClose = () => {
        setView(VIEWS.MAIN);
        setOpen(false);
    };

    const manualSections = {
        [VIEWS.MICROCLIMATE]: {
            title: translate("microclimate_definitions"),
            component: <MicroclimateSection />,
        },
    };

    return (
        <>
            <IconButton color="primary" onClick={() => setOpen(true)}>
                <MenuBook />
            </IconButton>

            <Drawer
                anchor="right"
                open={open}
                onClose={handleClose}
                transitionDuration={350}
                PaperProps={{
                    sx: {
                        width: expanded ? "100vw" : 420,
                        transition: "width 340ms cubic-bezier(0.22, 1, 0.36, 1)",
                        bgcolor: "background.default",
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                    },
                }}
            >
                <Box
                    sx={{
                        px: 2.5,
                        py: 2,
                        bgcolor: "primary.main",
                        color: "primary.contrastText",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexShrink: 0,
                    }}
                >
                    {expanded && (
                        <IconButton
                            size="small"
                            onClick={() => setView(VIEWS.MAIN)}
                            sx={{ color: "inherit" }}
                        >
                            <ArrowBack fontSize="small" />
                        </IconButton>
                    )}

                    <MenuBook fontSize="small" />

                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {expanded ? manualSections[view].title : translate("manual")}
                    </Typography>
                </Box>

                <Box sx={{ position: "relative", flex: 1, overflow: "hidden" }}>
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            p: 2.5,
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            transform: expanded ? "translateX(-80px)" : "translateX(0)",
                            opacity: expanded ? 0 : 1,
                            pointerEvents: expanded ? "none" : "auto",
                            transition: "transform 320ms cubic-bezier(0.22, 1, 0.36, 1), opacity 520ms ease",
                        }}
                    >
                        <ManualCard
                            icon={<TravelExplore fontSize="small" />}
                            label={translate("microclimate_definitions")}
                            onClick={() => setView(VIEWS.MICROCLIMATE)}
                        />
                    </Box>

                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            p: 4,
                            transform: expanded ? "translateX(0)" : "translateX(120px)",
                            opacity: expanded ? 1 : 0,
                            pointerEvents: expanded ? "auto" : "none",
                            transition: "transform 340ms cubic-bezier(0.22, 1, 0.36, 1), opacity 520ms ease",
                            overflowY: "auto",
                        }}
                    >
                        {expanded && manualSections[view].component}
                    </Box>
                </Box>
            </Drawer>
        </>
    );
}

function ManualCard({ icon, label, onClick }) {
    return (
        <Box
            onClick={onClick}
            sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: "background.paper",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": {
                    bgcolor: "neutral.main",
                    transform: "translateX(2px)",
                },
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                {icon}
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {label}
                </Typography>
            </Box>
            <ChevronRight fontSize="small" sx={{ opacity: 0.45 }} />
        </Box>
    );
}